import React, { useState, useEffect, useRef } from "react";
import { ApiService } from "../../services/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeHighlight from "rehype-highlight";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import {
  X,
  AlertCircle,
  Maximize2,
  Minimize2,
  Mic,
  MicOff,
  Square,
  Send,
  RefreshCw,
  Calendar,
  User,
  CalendarClock,
  CloudSun,
  Video,
  Loader2,
} from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";

interface ChatMessage {
  id: string;
  type: "user" | "assistant" | "system";
  content: string;
  timestamp: Date;
}

const AIAssistantPanel = ({
  isOpen,
  onClose,
  isHomePage = false,
  isExpanded = false,
  onToggleExpand,
  sidebarExpanded = true,
}: {
  isOpen: boolean;
  onClose?: () => void;
  isHomePage?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  sidebarExpanded?: boolean;
}) => {
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [typingMessage, setTypingMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [pendingAssistantText, setPendingAssistantText] = useState("");
  const typingCancelledRef = useRef(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const getMaxTextareaHeight = () => (isMobile ? 160 : 120);
  const { currentTheme } = useTheme();
  const { user } = useAuth();

  // Audio recording states
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (user?.id && !sessionId) {
      setSessionId(user.id);
    }
  }, [user, sessionId]);

  const loadChatHistory = async () => {
    if (!user?.id) return;

    setLoadingHistory(true);
    try {
      const response = await ApiService.getChatHistory();

      if (response && Array.isArray(response)) {
        const messages: ChatMessage[] = [];

        response.forEach((item: any) => {
          if (item.message && Array.isArray(item.message)) {
            item.message.forEach((msg: any) => {
              if (msg.role && msg.content) {
                messages.push({
                  id: crypto.randomUUID(),
                  type: msg.role === "user" ? "user" : "assistant",
                  content: msg.content,
                  timestamp: new Date(item.createdAt || Date.now()),
                });
              }
            });
          }
        });

        setChatHistory(messages);
      }
    } catch (error) {
      console.error("Geçmiş sohbetler yüklenirken hata:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (isOpen && user?.id && chatHistory.length === 0) {
      loadChatHistory();
    }
  }, [isOpen, user?.id]);

  useEffect(() => {
    const handleSendAIMessage = (event: CustomEvent) => {
      const { message: messageText } = event.detail || {};
      if (messageText && isOpen) {
        sendMessage(messageText);
      }
    };

    window.addEventListener("sendAIMessage", handleSendAIMessage as EventListener);

    return () => {
      window.removeEventListener("sendAIMessage", handleSendAIMessage as EventListener);
    };
  }, [isOpen, loading]);

  const startNewChat = () => {
    setChatHistory([]);
    setMessage("");
    setTypingMessage("");
    setIsTyping(false);
    setLoading(false);
  };

  const handleScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      const isAtBottom = scrollHeight - scrollTop <= clientHeight + 20;

      setAutoScroll(isAtBottom);
    }
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      requestAnimationFrame(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      });

      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  };

  useEffect(() => {
    if (!isTyping) {
      scrollToBottom();
    }
  }, [chatHistory]);

  useEffect(() => {
    if (autoScroll && !isTyping) {
      scrollToBottom();
    }
  }, [autoScroll]);

  useEffect(() => {
    if (isTyping && typingMessage) {
      if (chatContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
        const isAtBottom = scrollHeight - scrollTop <= clientHeight + 10;

        if (isAtBottom && autoScroll) {
          requestAnimationFrame(() => {
            if (chatContainerRef.current && autoScroll) {
              chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            }
          });
        }
      }
    }
  }, [typingMessage, autoScroll]);

  const cleanIncompleteMarkdown = (text: string): string => {
    let cleaned = text;
    cleaned = cleaned.replace(/\[([^\]]*?)$/g, "");
    cleaned = cleaned.replace(/\[([^\]]+)\]\([^\)]*$/g, (match, linkText) => linkText);
    return cleaned;
  };

  const convertMathNotation = (text: string): string => {
    let converted = text;

    converted = converted.replace(/\\\(([\s\S]+?)\\\)/g, (match, content) => `$${content}$`);
    converted = converted.replace(/\\\\\(([\s\S]+?)\\\\\)/g, (match, content) => `$${content}$`);
    converted = converted.replace(/\\\[([\s\S]+?)\\\]/g, (match, content) => `$$${content}$$`);
    converted = converted.replace(/\\\\\[([\s\S]+?)\\\\\]/g, (match, content) => `$$${content}$$`);

    converted = converted.replace(/\(([^()]*\\[a-zA-Z]+[^()]*)\)/g, (match, content, offset, fullText) => {
      const beforeChar = offset > 0 ? fullText[offset - 1] : " ";
      if (/[a-zA-Z0-9]/.test(beforeChar)) {
        return match;
      }
      if (content.match(/\\[a-zA-Z]+/)) {
        return `$${content}$`;
      }
      return match;
    });

    return converted;
  };

  const typeMessage = (text: string, callback: () => void) => {
    setLoading(false);
    setIsTyping(true);
    setTypingMessage("");
    setPendingAssistantText(text);
    typingCancelledRef.current = false;
    let i = 0;
    const typingSpeed = 6;

    const typeNextChar = () => {
      if (typingCancelledRef.current) {
        setIsTyping(false);
        setTypingMessage("");
        callback();
        setPendingAssistantText("");

        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
          }
        }, 100);
        return;
      }

      if (i < text.length) {
        const currentText = text.substring(0, i + 1);
        const cleanedText = cleanIncompleteMarkdown(currentText);
        setTypingMessage(cleanedText);
        i++;
        setTimeout(typeNextChar, typingSpeed);
      } else {
        setIsTyping(false);
        setTypingMessage("");
        callback();
        setPendingAssistantText("");

        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
          }
        }, 100);
      }
    };

    setTimeout(typeNextChar, 100);
  };

  const stopTypingNow = () => {
    if (!isTyping) return;
    typingCancelledRef.current = true;

    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 100);
  };

  const autoResizeTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    const maxH = getMaxTextareaHeight();
    el.style.height = "auto";
    const desired = Math.min(el.scrollHeight, maxH);
    el.style.height = `${desired}px`;
    el.style.overflowY = el.scrollHeight > maxH ? "auto" : "hidden";
  };

  useEffect(() => {
    autoResizeTextarea();
  }, [message, isMobile]);

  const addSystemMessage = (message: string) => {
    const systemMessage: ChatMessage = {
      id: crypto.randomUUID(),
      type: "system",
      content: message,
      timestamp: new Date(),
    };
    setChatHistory((prev) => [...prev, systemMessage]);
    setLoading(false);
    setIsTyping(false);
  };

  const sendMessage = async (messageText?: string) => {
    const textToSend = messageText || message.trim();
    if (!textToSend || loading) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      type: "user",
      content: textToSend,
      timestamp: new Date(),
    };

    setChatHistory((prev) => [...prev, userMessage]);
    setMessage("");
    setLoading(true);
    setAutoScroll(true);

    try {
      const response = await ApiService.sendChat({
        action: "sendMessage",
        sessionId: sessionId,
        message: textToSend,
      });

      if (!response || !response.output || typeof response.output !== "string") {
        console.error("AI Asistan yanıt hatası: response.output bulunamadı", response);
        addSystemMessage("Yanıt oluşturulurken bir sorun oluştu. Lütfen tekrar deneyin.");
        return;
      }

      typeMessage(response.output, () => {
        const assistantMessage: ChatMessage = {
          id: crypto.randomUUID(),
          type: "assistant",
          content: response.output,
          timestamp: new Date(),
        };
        setChatHistory((prev) => [...prev, assistantMessage]);
      });
    } catch (error) {
      console.error("AI Asistan hatası:", error);
      addSystemMessage("Bağlantı hatası oluştu. Lütfen internet bağlantınızı kontrol edin ve tekrar deneyin.");
    }
  };

  const templateQuestions = [
    { icon: Video, text: "Bugünkü toplantılarım neler?" },
    { icon: Calendar, text: "Takvim etkinliklerim neler?" },
    { icon: CalendarClock, text: "Yaklaşan randevularım neler?" },
  ];

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          setIsTranscribing(true);
          
          try {
            const result = await ApiService.transcribeAudio(audioBlob, 'tr');
            if (result.text) {
              setMessage(prev => prev ? `${prev} ${result.text}` : result.text);
              setTimeout(() => {
                if (textareaRef.current) {
                  textareaRef.current.focus();
                  autoResizeTextarea();
                }
              }, 100);
            }
          } catch (error: any) {
            console.error('Transkripsiyon hatası:', error);
            addSystemMessage('Ses yazıya çevrilirken bir hata oluştu. Lütfen tekrar deneyin.');
          } finally {
            setIsTranscribing(false);
          }
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error: any) {
      console.error('Mikrofon erişim hatası:', error);
      addSystemMessage('Mikrofona erişilemedi. Lütfen mikrofon izinlerini kontrol edin.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@600;700&display=swap');
          
          .chat-container::-webkit-scrollbar {
            display: none;
          }
          .chat-container {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          @keyframes shimmer {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(100%);
            }
          }
          .animate-shimmer {
            animation: shimmer 3s ease-in-out infinite;
          }
          .logo-text {
            font-family: 'Poppins', sans-serif;
            font-weight: 700;
            letter-spacing: -0.02em;
          }
        `}
      </style>

      {isMobile && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden" onClick={onClose} />}

      <div
        className={`fixed z-50 transition-all duration-500 ease-in-out ${
          isMobile
            ? "inset-0 top-[60px]"
            : isExpanded
            ? `top-[60px] right-0 bottom-0 ${sidebarExpanded ? "md:left-64" : "md:left-[50px]"}`
            : "top-[60px] right-0 bottom-0 w-80"
        }`}>
        <div
          className={`flex flex-col h-full shadow-2xl w-full transition-all duration-500 ease-in-out ${
            isHomePage ? "backdrop-blur-lg" : "bg-gray-50"
          } ${isMobile ? "rounded-none p-0" : isHomePage ? "border border-white/25 p-1" : "border border-gray-200 p-1"}`}
          style={
            isHomePage
              ? {
                  background: `linear-gradient(135deg, ${currentTheme.colors.background.base}26, ${currentTheme.colors.background.via}33, ${currentTheme.colors.background.to}26)`,
                }
              : undefined
          }>
          <div className={`flex-shrink-0 relative px-6 py-4 sm:py-6 transition-all duration-500 ease-in-out`}>
            <div className="relative flex items-center justify-end">
              <div className={`absolute transition-all duration-500 ease-in-out ${isExpanded ? "left-1/2 -translate-x-1/2" : "left-0"}`}>
                <h2
                  className="logo-text text-2xl sm:text-3xl"
                  style={{
                    background: isHomePage
                      ? `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`
                      : `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}>
                  Deep
                </h2>
              </div>

              <div className="flex items-center space-x-2 relative z-10">
                {!isMobile && onToggleExpand && (
                  <button
                    onClick={onToggleExpand}
                    className={`p-1.5 rounded transition-colors ${
                      isHomePage
                        ? "text-gray-200 hover:text-white hover:bg-white/15"
                        : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    }`}
                    aria-label={isExpanded ? "Küçült" : "Genişlet"}
                    title={isExpanded ? "Küçült" : "Genişlet"}>
                    {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                  </button>
                )}
                {chatHistory.length > 0 && (
                  <button
                    onClick={startNewChat}
                    disabled={loading || isTyping}
                    className={`p-1.5 rounded transition-colors ${
                      loading || isTyping
                        ? isHomePage
                          ? "text-gray-200 cursor-not-allowed"
                          : "text-gray-400 cursor-not-allowed"
                        : isHomePage
                        ? "text-gray-200 hover:text-white hover:bg-white/15"
                        : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    }`}
                    aria-label="Yeni Sohbet"
                    title="Yeni Sohbet Başlat">
                    <RefreshCw size={16} />
                  </button>
                )}
                {onClose && (
                  <button
                    onClick={onClose}
                    className={`p-1.5 rounded transition-colors ${
                      isHomePage
                        ? "text-gray-200 hover:text-white hover:bg-white/15"
                        : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    }`}
                    aria-label="Kapat">
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div
            ref={chatContainerRef}
            onScroll={handleScroll}
            className={`chat-container flex-1 overflow-y-auto px-3 sm:px-4 py-2 pb-6 space-y-3 sm:space-y-4 transition-all duration-500 ease-in-out ${
              isExpanded ? "max-w-2xl mx-auto w-full" : ""
            }`}
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
            }}>
            <style>{`
              .chat-container::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            {loadingHistory ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 rounded-full animate-bounce" style={{ backgroundColor: currentTheme.colors.primary }}></div>
                  <div
                    className="w-3 h-3 rounded-full animate-bounce"
                    style={{
                      backgroundColor: currentTheme.colors.secondary,
                      animationDelay: "0.1s",
                    }}></div>
                  <div
                    className="w-3 h-3 rounded-full animate-bounce"
                    style={{
                      backgroundColor: currentTheme.colors.accent,
                      animationDelay: "0.2s",
                    }}></div>
                </div>
                <p className={`mt-4 text-sm ${isHomePage ? "text-gray-200" : "text-gray-600"}`}>Geçmiş sohbetler yükleniyor...</p>
              </div>
            ) : chatHistory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6 sm:py-8 px-4 space-y-4 sm:space-y-6">
                <div
                  className={`rounded-2xl p-6 sm:p-8 text-center max-w-sm mx-auto shadow-lg ${
                    isHomePage ? "backdrop-blur-md border border-white/20" : "border border-gray-200"
                  }`}
                  style={
                    isHomePage
                      ? {
                          background: `linear-gradient(135deg, ${currentTheme.colors.primary}26, ${currentTheme.colors.secondary}1f, ${currentTheme.colors.accent}26)`,
                        }
                      : {
                          background: `linear-gradient(135deg, ${currentTheme.colors.primary}15, ${currentTheme.colors.secondary}10, ${currentTheme.colors.accent}15)`,
                          borderColor: `${currentTheme.colors.primary}30`,
                        }
                  }>
                  <h3
                    className={`text-lg font-semibold mb-3 ${isHomePage ? "text-white" : "text-gray-900"}`}
                    style={
                      isHomePage
                        ? {
                            background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }
                        : {
                            background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }
                    }>
                    Merhaba {user?.firstName || "Kullanıcı"}! 👋
                  </h3>
                  <p className={`text-sm leading-relaxed ${isHomePage ? "text-gray-200" : "text-gray-700"}`}>
                    Deep asistanınız burada. Sorularınızı yazabilir, fikirlerinizi paylaşabilir ve yardım isteyebilirsiniz.
                  </p>
                </div>

                <div className="w-full max-w-md mx-auto">
                  <h4 className={`text-xs font-semibold mb-3 text-center ${isHomePage ? "text-gray-300" : "text-gray-600"}`}>
                    Hızlı Başlangıç
                  </h4>
                  <div className="flex flex-col gap-2">
                    {templateQuestions.map((question, index) => {
                      const IconComponent = question.icon;
                      return (
                        <button
                          key={index}
                          onClick={() => sendMessage(question.text)}
                          disabled={loading || isTyping}
                          className={`group p-3 sm:p-4 rounded-xl text-left transition-all duration-200 shadow-lg ${
                            isHomePage
                              ? "backdrop-blur-md border border-white/20 hover:bg-white/10"
                              : "border border-gray-200 hover:shadow-xl"
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                          style={
                            isHomePage
                              ? {
                                  background: `linear-gradient(135deg, ${currentTheme.colors.primary}26, ${currentTheme.colors.secondary}1f, ${currentTheme.colors.accent}26)`,
                                }
                              : {
                                  background: `linear-gradient(135deg, ${currentTheme.colors.primary}15, ${currentTheme.colors.secondary}10, ${currentTheme.colors.accent}15)`,
                                  borderColor: `${currentTheme.colors.primary}30`,
                                }
                          }>
                          <div className="flex items-center gap-2 sm:gap-3">
                            <IconComponent
                              size={20}
                              className={`${isHomePage ? "text-white group-hover:text-white" : "text-gray-600 group-hover:text-gray-900"}`}
                            />
                            <span
                              className={`text-xs sm:text-sm font-medium ${
                                isHomePage ? "text-white group-hover:text-white" : "text-gray-700 group-hover:text-gray-900"
                              }`}>
                              {question.text}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null}

            {chatHistory.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.type === "user" ? "justify-end" : msg.type === "system" ? "justify-center" : "justify-start"}`}>
                <div
                  className={`rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-sm ${
                    msg.type === "system"
                      ? "max-w-[80%] bg-amber-50 border border-amber-300 text-amber-900 shadow-md"
                      : msg.type === "assistant"
                      ? "max-w-[75%] text-white backdrop-blur-sm shadow-lg"
                      : "max-w-[75%] text-white backdrop-blur-sm shadow-md"
                  }`}
                  style={
                    msg.type === "assistant"
                      ? {
                          background: `${currentTheme.colors.primary}e6`,
                          borderColor: `${currentTheme.colors.primary}80`,
                          borderWidth: "1px",
                          borderStyle: "solid",
                        }
                      : msg.type === "user"
                      ? {
                          background: `${currentTheme.colors.secondary}e6`,
                          borderColor: `${currentTheme.colors.secondary}80`,
                          borderWidth: "1px",
                          borderStyle: "solid",
                        }
                      : msg.type === "system" && isHomePage
                      ? {
                          backgroundColor: "rgba(252, 211, 77, 0.15)",
                          backdropFilter: "blur(10px)",
                          borderColor: "#fbbf24",
                          borderWidth: "1px",
                          borderStyle: "solid",
                        }
                      : undefined
                  }>
                  {msg.type === "system" ? (
                    <div className="flex items-start gap-3">
                      <AlertCircle className={`flex-shrink-0 mt-0.5 ${isHomePage ? "text-amber-300" : "text-amber-600"}`} size={18} />
                      <div className="flex-1">
                        <div className={`font-semibold text-sm mb-1 ${isHomePage ? "text-amber-100" : "text-amber-900"}`}>
                          Sistem Mesajı
                        </div>
                        <div className={`text-xs leading-relaxed ${isHomePage ? "text-amber-50" : "text-amber-800"}`}>{msg.content}</div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="leading-relaxed prose prose-invert prose-sm max-w-none overflow-hidden">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm, remarkMath]}
                          rehypePlugins={[rehypeHighlight, rehypeKatex]}
                          components={{
                            h1: ({ children }) => <h1 className="text-2xl font-bold text-white mt-4 mb-2">{children}</h1>,
                            h2: ({ children }) => <h2 className="text-xl font-semibold text-white mt-4 mb-2">{children}</h2>,
                            h3: ({ children }) => <h3 className="text-lg font-semibold text-white mt-4 mb-2">{children}</h3>,
                            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                            code: ({ children, className }) =>
                              className ? (
                                <code className="bg-gray-800 text-gray-100 px-2 py-1 rounded text-sm">{children}</code>
                              ) : (
                                <code className="bg-gray-700 text-gray-200 px-1 py-0.5 rounded text-sm">{children}</code>
                              ),
                            pre: ({ children }) => (
                              <pre className="bg-gray-800 text-gray-100 p-3 rounded-lg overflow-x-auto text-sm mb-2">{children}</pre>
                            ),
                            ul: ({ children }) => <ul className="list-disc ml-6 mb-2">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal ml-6 mb-2">{children}</ol>,
                            li: ({ children }) => <li className="mb-1">{children}</li>,
                            a: ({ children, href }) => (
                              <a
                                href={href}
                                className="underline font-semibold"
                                style={{
                                  color: "#ffffff",
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                }}>
                                {children}
                              </a>
                            ),
                            strong: ({ children }) => <strong className="font-bold text-white">{children}</strong>,
                            em: ({ children }) => <em className="italic">{children}</em>,
                          }}>
                          {convertMathNotation(msg.content)}
                        </ReactMarkdown>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div
                  className="rounded-xl px-4 py-3 text-sm shadow-md max-w-[75%] text-white backdrop-blur-sm"
                  style={{
                    background: `${currentTheme.colors.primary}e6`,
                    borderColor: `${currentTheme.colors.primary}80`,
                    borderWidth: "1px",
                    borderStyle: "solid",
                  }}>
                  <div className="flex items-center space-x-3">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: "#ffffff" }}></div>
                      <div
                        className="w-2 h-2 rounded-full animate-bounce"
                        style={{
                          backgroundColor: "#f0f0f0",
                          animationDelay: "0.1s",
                        }}></div>
                      <div
                        className="w-2 h-2 rounded-full animate-bounce"
                        style={{
                          backgroundColor: "#e0e0e0",
                          animationDelay: "0.2s",
                        }}></div>
                    </div>
                    <span>Düşünüyor...</span>
                  </div>
                </div>
              </div>
            )}

            {isTyping && (
              <div className="flex justify-start">
                <div
                  className="rounded-xl px-4 py-3 text-sm shadow-md max-w-[75%] text-white backdrop-blur-sm"
                  style={{
                    background: `${currentTheme.colors.primary}e6`,
                    borderColor: `${currentTheme.colors.primary}80`,
                    borderWidth: "1px",
                    borderStyle: "solid",
                  }}>
                  <div className="leading-relaxed prose prose-invert prose-sm max-w-none overflow-hidden">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkMath]}
                      rehypePlugins={[rehypeHighlight, rehypeKatex]}
                      components={{
                        h1: ({ children }) => <h1 className="text-2xl font-bold text-white mt-4 mb-2">{children}</h1>,
                        h2: ({ children }) => <h2 className="text-xl font-semibold text-white mt-4 mb-2">{children}</h2>,
                        h3: ({ children }) => <h3 className="text-lg font-semibold text-white mt-4 mb-2">{children}</h3>,
                        p: ({ children }) => <p className="mb-2 last:mb-0 inline">{children}</p>,
                        code: ({ children, className }) =>
                          className ? (
                            <code className="bg-gray-800 text-gray-100 px-2 py-1 rounded text-sm">{children}</code>
                          ) : (
                            <code className="bg-gray-700 text-gray-200 px-1 py-0.5 rounded text-sm">{children}</code>
                          ),
                        pre: ({ children }) => (
                          <pre className="bg-gray-800 text-gray-100 p-3 rounded-lg overflow-x-auto text-sm mb-2">{children}</pre>
                        ),
                        ul: ({ children }) => <ul className="list-disc ml-6 mb-2">{children}</ul>,
                        ol: ({ children }) => <ol className="list-decimal ml-6 mb-2">{children}</ol>,
                        li: ({ children }) => <li className="mb-1">{children}</li>,
                        a: ({ children, href }) => (
                          <a
                            href={href}
                            className="underline font-semibold"
                            style={{
                              color: "#ffffff",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                            }}>
                            {children}
                          </a>
                        ),
                        strong: ({ children }) => <strong className="font-bold text-white">{children}</strong>,
                        em: ({ children }) => <em className="italic">{children}</em>,
                      }}>
                      {convertMathNotation(typingMessage)}
                    </ReactMarkdown>
                    <span className="inline-block w-2 h-4 animate-pulse align-middle" style={{ backgroundColor: "#ffffff" }}>
                      |
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            className={`flex-shrink-0 px-4 pt-4 pb-4 transition-all duration-500 ease-in-out ${
              isExpanded ? "max-w-2xl mx-auto w-full" : ""
            }`}
            style={{
              paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
            }}>
            <div
              className={`rounded-3xl shadow-sm flex items-center gap-3 px-4 py-3 ${
                isHomePage ? "backdrop-blur-md bg-white/12 border border-white/25" : "bg-white border border-gray-300"
              }`}>
              <textarea
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  autoResizeTextarea();
                }}
                onKeyPress={handleKeyPress}
                disabled={loading || isTyping}
                ref={textareaRef}
                rows={1}
                onInput={autoResizeTextarea}
                className={`bg-transparent text-sm focus:outline-none resize-none w-full disabled:opacity-50 ${
                  isHomePage ? "text-white placeholder-gray-300" : "text-gray-900 placeholder-gray-500"
                }`}
                placeholder="Mesajınızı yazın..."
                style={{ height: "auto", overflow: "hidden", maxHeight: `${getMaxTextareaHeight()}px` }}
              />

              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={toggleRecording}
                  disabled={loading || isTyping || isTranscribing}
                  className={`rounded-full flex items-center justify-center transition-all duration-200 w-8 h-8 ${
                    isRecording
                      ? "bg-red-500 hover:bg-red-600 animate-pulse"
                      : isTranscribing
                      ? "bg-blue-500 cursor-wait"
                      : isHomePage
                      ? "hover:bg-white/10"
                      : "hover:bg-gray-100"
                  } disabled:opacity-50`}
                  title={isRecording ? "Kaydı Durdur" : isTranscribing ? "Yazıya çevriliyor..." : "Ses Kaydı"}>
                  {isTranscribing ? (
                    <Loader2 size={20} className="text-white animate-spin" />
                  ) : isRecording ? (
                    <MicOff size={20} className="text-white" />
                  ) : (
                    <Mic size={20} className={isHomePage ? "text-white" : "text-gray-500"} />
                  )}
                </button>

                {isTyping ? (
                  <button
                    onClick={stopTypingNow}
                    className="rounded-full flex items-center justify-center transition-all duration-200 w-8 h-8 bg-red-500 hover:bg-red-600"
                    title="Durdur">
                    <Square size={16} className="text-white fill-white" />
                  </button>
                ) : (
                  <button
                    onClick={() => sendMessage()}
                    disabled={!message.trim() || loading}
                    className={`rounded-full flex items-center justify-center transition-all duration-200 w-8 h-8 ${
                      !message.trim() || loading
                        ? isHomePage
                          ? "bg-white/20 cursor-not-allowed"
                          : "bg-gray-200 cursor-not-allowed"
                        : isHomePage
                        ? "bg-white text-gray-900 hover:bg-gray-100"
                        : "hover:bg-gray-100"
                    }`}
                    style={
                      !message.trim() || loading
                        ? undefined
                        : !isHomePage
                        ? {
                            backgroundColor: currentTheme.colors.primary,
                          }
                        : undefined
                    }
                    title="Gönder">
                    <Send
                      size={16}
                      className={
                        !message.trim() || loading
                          ? isHomePage
                            ? "text-white/50"
                            : "text-gray-400"
                          : isHomePage
                          ? "text-gray-900"
                          : "text-white"
                      }
                    />
                  </button>
                )}
              </div>
            </div>

            <p className={`text-xs text-center mt-2 ${isHomePage ? "text-gray-300" : "text-gray-500"}`}>
              Deep hata yapabilir. Önemli bilgileri kontrol edin.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default AIAssistantPanel;
